import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CaretLeftIcon,
  PhoneCallIcon,
  EnvelopeSimpleIcon,
  TrashIcon,
  ChatCircleIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';
import {
  createNote,
  deleteNote,
  fetchGuest,
  fetchGuests,
  patchGuest,
  type Guest,
  type GuestFlags,
  type GuestNote,
} from '@/features/clients/api';
import { Button, Card, CardContent, Input, Label, SkeletonRows, StatusChip, cn, fmtDate, fmtMoney } from '@smena/ui';

export function ClientsPage() {
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const listQ = useQuery({
    queryKey: ['guests', q],
    queryFn: () => fetchGuests(q || undefined),
    refetchInterval: 30_000,
  });

  return (
    // Две жёстких колонки схлопывали список на узком экране: 340px забирала левая,
    // правой не оставалось ничего. На мобильном - одна колонка, как в «Диалогах».
    <div className="grid h-full min-h-0 grid-cols-1 gap-4 md:grid-cols-[340px_minmax(0,1fr)]">
      <Card className={cn('min-h-0 overflow-hidden', selectedId && 'hidden md:block')}>
        <div className="flex h-full min-h-0 flex-col">
          <div className="border-b border-line p-3">
            <Input
              placeholder="Поиск: имя / телефон / email"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {listQ.isLoading ? (
              <SkeletonRows rows={5} />
            ) : !listQ.data || listQ.data.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted">
                Никого не нашлось. Гости появляются здесь сами, вместе с первыми бронями.
              </div>
            ) : (
              listQ.data.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setSelectedId(g.id)}
                  className={cn(
                    'flex w-full flex-col gap-0.5 border-b border-line/50 p-3 text-left transition-colors hover:bg-panel2',
                    selectedId === g.id && 'bg-panel2',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium">
                      {g.name ?? 'Гость без имени'}
                    </span>
                    <FlagsInline flags={g.flags} />
                  </div>
                  <div className="text-xs text-muted">{g.phone ?? g.email ?? '-'}</div>
                  {g.tags.length > 0 ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {g.tags.map((t) => (
                        <span key={t} className="rounded bg-panel2 px-1.5 py-0.5 text-[10px] text-muted">
                          {t}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      </Card>

      <Card className={cn('min-h-0 overflow-hidden', !selectedId && 'hidden md:block')}>
        {selectedId ? (
          <ClientDetailPane key={selectedId} guestId={selectedId} onBack={() => setSelectedId(null)} />
        ) : (
          <CardContent className="pt-6 text-center text-sm text-muted">
            ← Выберите клиента слева
          </CardContent>
        )}
      </Card>
    </div>
  );
}

function FlagsInline({ flags }: { flags: GuestFlags }) {
  const items: Array<{ key: string; tone: Parameters<typeof StatusChip>[0]['tone']; label: string }> = [];
  if (flags.vip) items.push({ key: 'vip', tone: 'accent', label: 'VIP' });
  if (flags.always_escalate) items.push({ key: 'esc', tone: 'red', label: 'Только человек' });
  if (flags.blocklist) items.push({ key: 'bl', tone: 'red', label: 'В блоке' });
  if (flags.do_not_initiate) items.push({ key: 'noinit', tone: 'muted', label: 'Не писать первым' });
  if (items.length === 0) return null;
  return (
    <div className="flex gap-1">
      {items.map((i) => (
        <StatusChip key={i.key} tone={i.tone}>
          {i.label}
        </StatusChip>
      ))}
    </div>
  );
}

function ClientDetailPane({ guestId, onBack }: { guestId: string; onBack: () => void }) {
  const qc = useQueryClient();
  const detailQ = useQuery({ queryKey: ['guest', guestId], queryFn: () => fetchGuest(guestId) });

  const patch = useMutation({
    mutationFn: (data: Partial<Pick<Guest, 'name' | 'phone' | 'email' | 'tags' | 'flags'>>) =>
      patchGuest(guestId, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['guest', guestId] });
      void qc.invalidateQueries({ queryKey: ['guests'] });
      toast.success('Сохранено');
    },
  });

  const [noteKind, setNoteKind] = useState<'note' | 'instruction'>('instruction');
  const [noteText, setNoteText] = useState('');
  const addNote = useMutation({
    mutationFn: () => createNote(guestId, { kind: noteKind, text: noteText.trim() }),
    onSuccess: () => {
      setNoteText('');
      void qc.invalidateQueries({ queryKey: ['guest', guestId] });
      toast.success(noteKind === 'instruction' ? 'Сохранили' : 'Заметка сохранена');
    },
  });

  const removeNote = useMutation({
    mutationFn: (noteId: string) => deleteNote(guestId, noteId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['guest', guestId] });
      toast.success('Удалено');
    },
  });

  if (detailQ.isLoading || !detailQ.data) {
    return <CardContent className="pt-6 text-center text-sm text-muted">Загрузка…</CardContent>;
  }

  const { guest, notes, bookings } = detailQ.data;
  const instructions = notes.filter((n) => n.kind === 'instruction');
  const staffNotes = notes.filter((n) => n.kind === 'note');

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Шапка */}
      <div className="border-b border-line p-5">
        <div className="flex items-baseline justify-between gap-3">
          <div className="flex min-w-0 items-baseline gap-2">
            <button
              type="button"
              onClick={onBack}
              className="-ml-1 shrink-0 self-start rounded-md p-1 text-muted hover:bg-panel2 hover:text-text md:hidden"
              aria-label="Назад к списку клиентов"
            >
              <CaretLeftIcon weight="bold" className="size-5" />
            </button>
            <div className="min-w-0">
            <div className="text-lg font-semibold">{guest.name ?? 'Гость без имени'}</div>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted">
              {guest.phone ? (
                <span className="flex items-center gap-1">
                  <PhoneCallIcon className="size-3.5" /> {guest.phone}
                </span>
              ) : null}
              {guest.email ? (
                <span className="flex items-center gap-1">
                  <EnvelopeSimpleIcon className="size-3.5" /> {guest.email}
                </span>
              ) : null}
              <span>{bookings.length} брон.</span>
            </div>
            </div>
          </div>
          <FlagsInline flags={guest.flags} />
        </div>

        {/* Флаги-тумблеры */}
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <FlagToggle
            label="VIP"
            value={!!guest.flags.vip}
            onChange={(v) => patch.mutate({ flags: { ...guest.flags, vip: v } })}
          />
          <FlagToggle
            label="Отвечает только человек"
            value={!!guest.flags.always_escalate}
            onChange={(v) => patch.mutate({ flags: { ...guest.flags, always_escalate: v } })}
          />
          <FlagToggle
            label="Не писать первым"
            value={!!guest.flags.do_not_initiate}
            onChange={(v) => patch.mutate({ flags: { ...guest.flags, do_not_initiate: v } })}
          />
          <FlagToggle
            label="Заблокирован"
            value={!!guest.flags.blocklist}
            onChange={(v) => patch.mutate({ flags: { ...guest.flags, blocklist: v } })}
          />
        </div>
      </div>

      {/* Скроллящаяся часть */}
      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        {/* Инструкции агенту */}
        <section className="mb-5">
          <div className="mb-2 flex items-center gap-2">
            <UsersThreeIcon weight="bold" className="size-4 text-accent-fg" />
            <h3 className="text-sm font-semibold uppercase tracking-wider">Что учесть Smena</h3>
            <span className="text-[11px] text-muted">Smena читает это перед каждым ответом</span>
          </div>
          {instructions.length === 0 ? (
            <div className="text-xs text-muted">
              Пока пусто. Например: «по любым вопросам об оплате звать человека».
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {instructions.map((n) => (
                <NoteRow key={n.id} note={n} onDelete={(id) => removeNote.mutate(id)} />
              ))}
            </div>
          )}
        </section>

        {/* Заметки для своих */}
        <section className="mb-5">
          <div className="mb-2 flex items-center gap-2">
            <ChatCircleIcon weight="bold" className="size-4 text-muted" />
            <h3 className="text-sm font-semibold uppercase tracking-wider">Заметки для своих</h3>
            <span className="text-[11px] text-muted">Smena их не видит</span>
          </div>
          {staffNotes.length === 0 ? (
            <div className="text-xs text-muted">Пусто.</div>
          ) : (
            <div className="flex flex-col gap-2">
              {staffNotes.map((n) => (
                <NoteRow key={n.id} note={n} onDelete={(id) => removeNote.mutate(id)} />
              ))}
            </div>
          )}
        </section>

        {/* Добавить */}
        <section className="mb-5 rounded-md border border-line bg-panel2/40 p-3">
          <Label>Новая {noteKind === 'instruction' ? 'что учесть Smena' : 'заметка для своих'}</Label>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={2}
            placeholder={
              noteKind === 'instruction'
                ? 'Например: «по вопросам об оплате звать человека»'
                : 'Внутренняя заметка - Smena её не увидит'
            }
            className="mt-1 w-full resize-y rounded-md border border-line-control bg-input px-3 py-2 text-sm"
          />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <select
              value={noteKind}
              onChange={(e) => setNoteKind(e.target.value as 'note' | 'instruction')}
              className="rounded border border-line-control bg-input px-2 py-1 text-xs"
            >
              <option value="instruction">Что учесть Smena</option>
              <option value="note">Заметка для своих</option>
            </select>
            <Button
              size="sm"
              onClick={() => addNote.mutate()}
              disabled={!noteText.trim() || addNote.isPending}
            >
              Добавить
            </Button>
          </div>
        </section>

        {/* Брони */}
        {bookings.length > 0 ? (
          <section>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider">Брони</h3>
            <div className="flex flex-col gap-1.5">
              {bookings.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center justify-between rounded-md border border-line bg-panel2/40 px-3 py-2 text-sm"
                >
                  <div>
                    {fmtDate(b.checkin)} → {fmtDate(b.checkout)} · {b.nights} ноч.
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted">
                    <span>{fmtMoney(b.amount)}</span>
                    <StatusChip tone={b.status === 'cancelled' ? 'red' : 'green'}>
                      {b.status}
                    </StatusChip>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

function NoteRow({ note, onDelete }: { note: GuestNote; onDelete: (id: string) => void }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-line bg-panel2/40 p-3">
      <div className="flex-1 whitespace-pre-wrap text-sm">{note.text}</div>
      <button
        type="button"
        onClick={() => onDelete(note.id)}
        className="text-muted hover:text-destructive"
        aria-label="Удалить"
      >
        <TrashIcon className="size-4" />
      </button>
    </div>
  );
}

function FlagToggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 transition-colors',
        value
          ? 'border-accent-fg bg-accent/20 text-accent-fg'
          : 'border-line text-muted hover:border-line-control hover:text-text',
      )}
    >
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      {label}
    </label>
  );
}
